import type { Locale } from 'date-fns';
import { format } from 'date-fns/format';
import { getMonth } from 'date-fns/getMonth';
import { getYear } from 'date-fns/getYear';
import { isPast } from 'date-fns/isPast';
import { isSameDay } from 'date-fns/isSameDay';
import { isWeekend } from 'date-fns/isWeekend';
import holidays from 'holidays-norway';
import * as React from 'react';
import type { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import { Controls } from '~/Partials/DatePicker/Components/Controls/Controls';
import { DatePickerFloating } from '~/Partials/DatePickerFloating/DatePickerFloating';
import { getToday } from '~/Partials/Utils/dateUtils';
import { capitalize } from '~/Partials/Utils/utils';
import styles from './BookMeetingDatePicker.module.css';

export interface BookMeetingDatePickerPropsTranslations {
    applyLabel: string;
    cancelLabel: string;
    nextMonth: string;
    prevMonth: string;
    chooseDayInMonth: string;
}

export interface BookMeetingDatePickerProps {
    open: boolean;
    activeDay: Date | null;
    changeActiveDay: (date: Date | null) => void;
    onClose: () => void;
    onCloseWithCheck: () => void;
    setFloatingElement: (element: HTMLDivElement) => void;
    locale: Locale;
    translations: BookMeetingDatePickerPropsTranslations;
    floatingStyles: React.CSSProperties;
    rootRef: React.RefObject<HTMLDivElement>;
}

export const BookMeetingDatePicker: React.FC<BookMeetingDatePickerProps> = props => {
    const {
        open,
        onClose,
        onCloseWithCheck,
        setFloatingElement,
        changeActiveDay,
        floatingStyles,
        activeDay,
        rootRef,
        locale,
        translations,
    } = props;

    const [selectedDay, setSelectedDay] = React.useState<Date | null>(activeDay);
    const [currentlyVisibleMonth, setCurrentlyVisibleMonth] = React.useState<Date>(getToday());

    const hasSelectionChanged = !!selectedDay;

    const handleAccept = () => {
        hasSelectionChanged && changeActiveDay(selectedDay);
        onClose();
    };

    const handlePreviousMonthClick = (datePickerProps: ReactDatePickerCustomHeaderProps) => {
        datePickerProps.decreaseMonth();
    };

    const handleNextMonthClick = (datePickerProps: ReactDatePickerCustomHeaderProps) => {
        datePickerProps.increaseMonth();
    };

    const handleMonthChange = (month: Date) => {
        if (getMonth(month) !== getMonth(currentlyVisibleMonth)) {
            setCurrentlyVisibleMonth(month);

            return;
        }

        return;
    };

    const nationalHolidays = React.useMemo(
        () => holidays(getYear(currentlyVisibleMonth)).map(holiday => new Date(holiday.date)),
        [currentlyVisibleMonth]
    );

    const shouldDayBeDisabled = (date: Date): boolean =>
        isWeekend(date) || isPast(date) || !!nationalHolidays.find(holidayDate => isSameDay(holidayDate, date));

    return (
        <DatePickerFloating
            config={{
                calendarClassName: styles.Calendar,
                weekDayClassName: styles.WeekDay,
                dayClassName: styles.Day,
                shouldDayBeDisabled,
            }}
            ariaHeader={{
                id: 'select-date',
                label: translations.chooseDayInMonth,
                headerType: 'h3',
            }}
            selectedDay={selectedDay}
            openToDate={activeDay ?? new Date()}
            filterDate={day => !shouldDayBeDisabled(day)}
            onChange={setSelectedDay}
            onMonthChange={handleMonthChange}
            floatingConfig={{
                rootRef,
                setFloatingElement,
                onClose,
                open,
                floatingStyles,
                className: styles.Dialog,
            }}
            headerProps={{
                onPreviousTimeFrameClick: headerProps => handlePreviousMonthClick(headerProps),
                onNextTimeFrameClick: headerProps => handleNextMonthClick(headerProps),
                dateRenderer: date =>
                    capitalize(
                        format(date, 'MMMM u', {
                            weekStartsOn: 1,
                            locale,
                        })
                    ),
                translations: {
                    nextTimeFrame: translations.nextMonth,
                    previousTimeFrame: translations.prevMonth,
                },
            }}
        >
            <Controls
                onCancel={onCloseWithCheck}
                onAccept={handleAccept}
                isAcceptActive={hasSelectionChanged}
                translations={{
                    accept: translations.applyLabel,
                    cancel: translations.cancelLabel,
                }}
            />
        </DatePickerFloating>
    );
};
